import React from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { CalendarDay } from '../types/schedule';

interface ScheduleCalendarProps {
  currentDate: Date;
  calendarDays: CalendarDay[];
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  onCurrentMonth: () => void;
  onDayClick?: (day: number) => void;
  selectedDay?: number;
}

const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
  currentDate,
  calendarDays,
  onPreviousMonth,
  onNextMonth,
  onCurrentMonth,
  onDayClick,
  selectedDay
}) => {
  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const monthName = monthNames[currentDate.getMonth()];
  const year = currentDate.getFullYear();

  // Agrupar dias em semanas
  const weeks = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    weeks.push(calendarDays.slice(i, i + 7));
  }


  return (
    <Card className="w-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Cronograma de Envios
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onCurrentMonth}
              className="text-xs"
            >
              Hoje
            </Button>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={onPreviousMonth}
                className="p-2"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onNextMonth}
                className="p-2"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800">
            {monthName} {year}
          </h2>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Cabeçalho dos dias da semana */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day) => (
            <div
              key={day}
              className="text-center text-sm font-medium text-gray-600 py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Grid do calendário */}
        <div className="grid grid-cols-7 gap-1">
          {weeks.map((week, weekIndex) => (
            <React.Fragment key={weekIndex}>
              {week.map((day, dayIndex) => (
                <div
                  key={`${weekIndex}-${dayIndex}`}
                  className={`
                    relative min-h-[60px] p-2 border rounded-lg cursor-pointer transition-all duration-200
                    ${day.isCurrentMonth 
                      ? 'bg-white hover:bg-gray-50' 
                      : 'bg-gray-50 text-gray-400'
                    }
                    ${day.isToday 
                      ? 'ring-2 ring-blue-500 bg-blue-50' 
                      : ''
                    }
                    ${selectedDay === day.day && day.isCurrentMonth
                      ? 'bg-blue-100 border-blue-300'
                      : ''
                    }
                    ${day.hasEvents && day.isCurrentMonth
                      ? 'border-green-300 bg-green-50'
                      : ''
                    }
                  `}
                  onClick={() => {
                    if (day.isCurrentMonth && onDayClick) {
                      onDayClick(day.day);
                    }
                  }}
                >
                  {/* Número do dia */}
                  <div className="text-sm font-medium mb-1">
                    {day.day}
                  </div>
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>

      </CardContent>
    </Card>
  );
};

export default ScheduleCalendar;

